/* The wishlist app injects its header icon before `cart-drawer-component` on
   mobile, which in Horizon sits inside the closed nav drawer, so the icon is
   never visible. Move it into the header's right column instead: that is also
   the app's own desktop target, and it is `display: contents` under 880px, so
   the icon becomes a grid item of the mobile header row. */
(() => {
  const TARGET = '#header-component .header__column--right';
  let queued = false;

  const place = () => {
    queued = false;

    const target = document.querySelector(TARGET);
    if (!target) return;

    const wrappers = document.querySelectorAll('.Vtl-WishlistHeaderWrapper');
    if (!wrappers.length) return;

    let placed = false;
    wrappers.forEach((wrapper) => {
      if (target.contains(wrapper)) {
        placed = true;
        return;
      }
      if (placed) {
        wrapper.remove();
        return;
      }
      target.appendChild(wrapper);
      placed = true;
    });
  };

  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(place);
  };

  schedule();
  new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
})();

/*
  Wishlist by Square (Vitals) injects collection-card hearts once on server-rendered
  product-cards. Nosto DynamicCard HTML loads later into #nosto-serp / #nosto-autocomplete,
  so hearts never appear there. Clone matching (or template) widgets onto Nosto cards
  so every suggestion/SERP card gets a heart without stripping the source card.
*/
(() => {
  const ROOTS = ['#nosto-serp', '#nosto-autocomplete'];
  const CARD = 'product-card[data-product-id]';
  const WIDGET = '.Vtl-CollectionCardWidget';
  let queued = false;
  let started = false;

  /**
   * @param {Element} card
   * @returns {string}
   */
  const getHandle = (card) => {
    try {
      const payload = card.getAttribute('view-event-payload');
      if (payload) {
        const handle = JSON.parse(payload)?.product?.handle;
        if (handle) return String(handle);
      }
    } catch {
      /* ignore */
    }
    const href = card.querySelector('a[href*="/products/"]')?.getAttribute('href') || '';
    const match = href.match(/\/products\/([^/?#]+)/);
    return match ? match[1] : 'product';
  };

  /**
   * @param {string} productId
   * @param {Element} nostoCard
   * @returns {Element | null}
   */
  const findSourceWidget = (productId, nostoCard) => {
    const cards = document.querySelectorAll(CARD);
    for (const card of cards) {
      if (card === nostoCard) continue;
      if (card.closest('#nosto-serp') || card.closest('#nosto-autocomplete')) continue;
      if (card.getAttribute('data-product-id') !== productId) continue;
      const widget = card.querySelector(WIDGET);
      if (widget) return widget;
    }
    return null;
  };

  /**
   * @returns {Element | null}
   */
  const findTemplateWidget = () => {
    const widgets = document.querySelectorAll(WIDGET);
    for (const widget of widgets) {
      if (widget.closest('#nosto-serp') || widget.closest('#nosto-autocomplete')) continue;
      return widget;
    }
    return widgets[0] || null;
  };

  /**
   * @param {Element} source
   * @param {string} productId
   * @param {string} handle
   * @returns {Element}
   */
  const cloneWidgetForProduct = (source, productId, handle) => {
    const node = /** @type {Element} */ (source.cloneNode(true));
    const safeHandle = handle || 'product';
    node.className = node.className.replace(
      /Vtl-WishlistIcon__Wrapper--Id-\S+/g,
      `Vtl-WishlistIcon__Wrapper--Id-${productId}-${safeHandle}`
    );
    node.classList.remove('Vtl-WishlistButton--active');
    node.querySelectorAll('.Vtl-WishlistButton--active').forEach((el) => {
      el.classList.remove('Vtl-WishlistButton--active');
    });
    const button = node.querySelector('.Vtl-WishlistButton__WishlistIconButton');
    if (button) button.setAttribute('title', 'add to wishlist');
    if (node instanceof HTMLElement) {
      node.style.left = '';
      node.style.top = '';
      node.style.right = '';
    }
    return node;
  };

  const syncNostoWishlist = () => {
    queued = false;
    const template = findTemplateWidget();

    ROOTS.forEach((selector) => {
      const root = document.querySelector(selector);
      if (!root) return;

      root.querySelectorAll(CARD).forEach((card) => {
        if (!(card instanceof HTMLElement)) return;
        if (card.querySelector(WIDGET)) return;

        const productId = card.getAttribute('data-product-id');
        if (!productId) return;

        const source = findSourceWidget(productId, card) || template;
        if (!source) return;

        card.appendChild(cloneWidgetForProduct(source, productId, getHandle(card)));
      });
    });
  };

  const schedule = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(syncNostoWishlist);
  };

  /**
   * @param {Element | null} root
   */
  const observeRoot = (root) => {
    if (!(root instanceof Element) || root.dataset.mgWishlistObserved === 'true') return;
    root.dataset.mgWishlistObserved = 'true';
    new MutationObserver(schedule).observe(root, { childList: true, subtree: true });
    schedule();
  };

  const observeAllRoots = () => {
    ROOTS.forEach((selector) => observeRoot(document.querySelector(selector)));
  };

  const start = () => {
    if (started) {
      observeAllRoots();
      return;
    }
    started = true;
    schedule();
    observeAllRoots();

    new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          for (const selector of ROOTS) {
            const id = selector.slice(1);
            const found = node.id === id ? node : node.querySelector?.(selector);
            if (found) observeRoot(found);
          }
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();

/* The app blanks its PDP wishlist button while the add/remove request is in
   flight, so the button collapses to a sliver and snaps back. Flag the wrapper
   for as long as that lasts: the CSS holds the button's box and shows a
   spinner in place of its label. */
(() => {
  const BUTTON = '.Vtl-WishlistButton__AddToWishlist';
  const WRAPPER = '.Vtl-WishlistButtonWrapper';
  const LOADING = 'mg-wishlist-loading';
  const MIN_MS = 400;
  const SETTLE_MS = 250;
  const MAX_MS = 5000;

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest && event.target.closest(BUTTON);
      if (!button) return;

      const wrapper = button.closest(WRAPPER);
      if (!wrapper || wrapper.classList.contains(LOADING)) return;

      wrapper.classList.add(LOADING);
      const start = performance.now();
      let settle;

      const finish = () => {
        clearTimeout(settle);
        clearTimeout(bail);
        observer.disconnect();
        wrapper.classList.remove(LOADING);
      };

      const check = () => {
        const current = wrapper.querySelector(BUTTON);
        if (!current || !current.textContent.trim()) return;
        settle = setTimeout(finish, Math.max(0, MIN_MS - (performance.now() - start)));
      };

      const observer = new MutationObserver(() => {
        clearTimeout(settle);
        settle = setTimeout(check, SETTLE_MS);
      });
      observer.observe(wrapper, { childList: true, subtree: true, characterData: true });

      const bail = setTimeout(finish, MAX_MS);
    },
    true
  );
})();

/* Wishlist ATC is owned by the Vitals app, which adds the item and then
   navigates to the cart page. Take the click, add through the Ajax cart,
   and let the theme refresh the drawer the same way a collection card does. */
(() => {
  const ITEM_BUTTON = '.Vtl-WishlistProduct__AddToCart';
  const ALL_BUTTON = '.Vtl-WishlistAddAllToCart';
  const CARD = '.Vtl-WishlistProduct';
  const CART_EVENT = 'shopify:cart:lines-update';

  /**
   * @param {Element} card
   * @returns {string | null}
   */
  const variantIdFromCard = (card) => {
    const href = card.querySelector('a[href*="variant="]')?.getAttribute('href') || '';
    try {
      return new URL(href, window.location.origin).searchParams.get('variant');
    } catch {
      return null;
    }
  };

  /**
   * @param {HTMLButtonElement} button
   * @returns {boolean}
   */
  const isUnavailable = (button) =>
    button.disabled ||
    button.classList.contains('Vtl-WishlistProduct__AddToCart--disabled') ||
    button.classList.contains('Vtl-WishlistAddAllToCart--disabled');

  const sectionIds = () => {
    /** @type {string[]} */
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) ids.push(item.dataset.sectionId);
    });
    return [...new Set(ids)];
  };

  /**
   * @param {string[]} variantIds
   */
  const addToCart = async (variantIds) => {
    const root = window.Shopify?.routes?.root || '/';
    const addUrl = window.Theme?.routes?.cart_add_url || `${root}cart/add.js`;
    const response = await fetch(addUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        items: variantIds.map((id) => ({ id: Number(id), quantity: 1 })),
        sections: sectionIds().join(','),
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json();
    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Add to cart failed');
    }
    return data;
  };

  const openCartDrawer = () => {
    /** @type {HTMLElement & { open?: () => void, isOpen?: boolean } | null} */
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (!drawer || typeof drawer.open !== 'function' || drawer.isOpen) return;
    drawer.open();
  };

  document.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const itemButton = target.closest(ITEM_BUTTON);
      const allButton = itemButton ? null : target.closest(ALL_BUTTON);
      const button = itemButton || allButton;
      if (!(button instanceof HTMLButtonElement) || isUnavailable(button)) return;
      if (button.dataset.mgWishlistAdding === 'true') return;

      /** @type {string[]} */
      let variantIds = [];
      if (allButton) {
        document.querySelectorAll(CARD).forEach((card) => {
          const cardButton = card.querySelector(ITEM_BUTTON);
          if (cardButton instanceof HTMLButtonElement && isUnavailable(cardButton)) return;
          const id = variantIdFromCard(card);
          if (id) variantIds.push(id);
        });
      } else {
        const card = button.closest(CARD);
        const id = card && variantIdFromCard(card);
        if (id) variantIds = [id];
      }
      if (!variantIds.length) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      button.dataset.mgWishlistAdding = 'true';
      button.disabled = true;

      /** @type {(value?: unknown) => void} */
      let resolvePromise = () => {};
      /** @type {(reason?: unknown) => void} */
      let rejectPromise = () => {};
      const promise = new Promise((resolve, reject) => {
        resolvePromise = resolve;
        rejectPromise = reject;
      });

      const cartEvent = new CustomEvent(CART_EVENT, { bubbles: true, cancelable: true });
      cartEvent.action = 'add';
      cartEvent.promise = promise;
      cartEvent.lines = variantIds.map((id) => ({ merchandiseId: id, quantity: 1 }));
      document.dispatchEvent(cartEvent);

      addToCart(variantIds)
        .then(async (data) => {
          const root = window.Shopify?.routes?.root || '/';
          const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((response) =>
            response.json()
          );
          resolvePromise({
            cart: { totalQuantity: cart.item_count },
            detail: {
              sections: data.sections,
              didError: false,
              items: cart.items,
              itemCount: variantIds.length,
            },
          });
          queueMicrotask(openCartDrawer);
        })
        .catch((error) => {
          rejectPromise(error);
        })
        .finally(() => {
          button.disabled = false;
          delete button.dataset.mgWishlistAdding;
        });
    },
    true
  );
})();
